import { CommonModule, Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AceTranslatePipe,
  AppLanguageService,
  BreadcrumbComponent,
  ButtonComponent,
  TranslationService
} from '@ace/ace';
import { Subscription } from 'rxjs';
import { environment } from '../../../environments/environments';
import { ToastrService } from 'ngx-toastr';
import {
  CopilotSettingsPayload,
  CopilotSettingsService,
  DEFAULT_COPILOT_AVATAR_LANGUAGE,
  DEFAULT_COPILOT_SPEAKER_LANGUAGE,
  DEFAULT_AVATAR_DIALECT
} from '../../services/copilot-settings.service';
import {
  AccessibilitySettings,
  AccessibilitySettingsService,
} from '../../services/accessibility-settings.service';

interface LanguageOption {
  id: number;
  name: string;
  code: string;
  direction: string | null;
}

type AvatarDialect = 'msa' | 'egyptian' | 'saudi' | 'uae' | 'bahraini' | 'lebanese';
type SettingsTab = 'general' | 'accessibility';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AceTranslatePipe,
    BreadcrumbComponent,
    ButtonComponent
  ],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit, OnDestroy {
  @Input() closeMode: 'back' | 'emit' = 'back';
  @Output() closed = new EventEmitter<void>();

  readonly systemLanguages = [
    { code: 'ar', label: 'Arabic', flag: '🇪🇬' },
    { code: 'en', label: 'English', flag: '🇬🇧' },
    { code: 'fr', label: 'French', flag: '🇫🇷' },
    { code: 'de', label: 'German', flag: '🇩🇪' },
  ];

  readonly avatarDialects: { value: AvatarDialect; label: string }[] = [
    { value: 'msa', label: 'Arabic - MSA' },
    { value: 'egyptian', label: 'Arabic - Egyptian' },
    { value: 'saudi', label: 'Arabic - Saudi' },
    { value: 'uae', label: 'Arabic - Emirati' },
    { value: 'bahraini', label: 'Arabic - Bahraini' },
    { value: 'lebanese', label: 'Arabic - Lebanese' },
  ];

  readonly textSpacingLevelCount = 4;
  readonly bigTextLevelCount = 4;
  readonly lineHeightLevelCount = 3;
  readonly textSpacingProgress = Array.from({ length: this.textSpacingLevelCount });
  readonly bigTextProgress = Array.from({ length: this.bigTextLevelCount });
  readonly lineHeightProgress = Array.from({ length: this.lineHeightLevelCount });
  readonly tabs: { id: SettingsTab; label: string }[] = [
    { id: 'general', label: 'General' },
    { id: 'accessibility', label: 'Accessibility' },
  ];

  activeTab: SettingsTab = 'general';
  breadcrumbItems: { label: string }[] = [];
  languageOptions: LanguageOption[] = [];
  formState: CopilotSettingsPayload = {
    speakerLanguage: DEFAULT_COPILOT_SPEAKER_LANGUAGE,
    avatarLanguage: DEFAULT_COPILOT_AVATAR_LANGUAGE,
    avatarDialect: '',
    systemLanguage: 'en',
  };
  accessibilitySettings: AccessibilitySettings = {
    textSpacing: 0,
    dyslexiaFont: false,
    lineHeight: 0,
    bigText: 0,
  };
  isLoading = false;
  isSaving = false;
  loadError: string | null = null;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private http: HttpClient,
    private appLanguageService: AppLanguageService,
    private translationService: TranslationService,
    private toastr: ToastrService,
    private copilotSettingsService: CopilotSettingsService,
    private accessibilitySettingsService: AccessibilitySettingsService,
    private location: Location,
  ) {}

  ngOnInit(): void {
    document.body.style.overflow = 'hidden';
    this.formState = this.normalizeSettings(this.copilotSettingsService.getSettings());
    this.accessibilitySettings = this.accessibilitySettingsService.getSettings();
    this.rebuildBreadcrumb();
    this.subscriptions.push(
      this.appLanguageService.selectedLanguage$.subscribe(() => this.rebuildBreadcrumb())
    );
    this.loadLanguages();
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  @HostListener('document:keydown.escape')
  closeSettings(): void {
    this.closed.emit();
    if (this.closeMode === 'back') {
      this.location.back();
    }
  }

  get avatarDialectEnabled(): boolean {
    return this.isArabicLanguageCode(this.formState.avatarLanguage);
  }

  setActiveTab(tab: SettingsTab): void {
    this.activeTab = tab;
  }

  get selectedAvatarDialect(): string {
    return this.avatarDialectEnabled
      ? this.formState.avatarDialect || DEFAULT_AVATAR_DIALECT
      : '';
  }

  set selectedAvatarDialect(value: string) {
    if (this.isAvatarDialect(value)) {
      this.formState.avatarDialect = value;
    }
  }

  loadLanguages(): void {
    this.isLoading = true;
    this.loadError = null;

    this.http.get<any>(`${environment.baseUrl}/avatar/api/v1/language`).subscribe({
      next: response => {
        this.languageOptions = this.parseLanguageOptions(response);
        this.isLoading = false;
      },
      error: () => {
        this.loadError = this.translationService.translate('Failed to load settings. Please try again.');
        this.isLoading = false;
      }
    });
  }

  onAvatarLanguageChange(nextLanguage: string): void {
    this.formState.avatarLanguage = nextLanguage;
    if (this.isArabicLanguageCode(nextLanguage) && !this.isAvatarDialect(this.formState.avatarDialect)) {
      this.formState.avatarDialect = DEFAULT_AVATAR_DIALECT;
    }
  }

  cycleAccessibilityLevel(
    key: 'textSpacing' | 'bigText' | 'lineHeight',
    levelCount: number,
  ): void {
    const current = this.accessibilitySettings[key];
    const next = ((current + 1) % levelCount) as AccessibilitySettings[typeof key];
    this.accessibilitySettings = this.accessibilitySettingsService.updateSettings({ [key]: next });
  }

  toggleDyslexiaFont(): void {
    this.accessibilitySettings = this.accessibilitySettingsService.updateSettings({
      dyslexiaFont: !this.accessibilitySettings.dyslexiaFont,
    });
  }

  resetAccessibility(): void {
    this.accessibilitySettings = this.accessibilitySettingsService.resetSettings();
  }

  getAccessibilityLevelLabel(current: number, total: number): string {
    return `${this.translationService.translate('Level')} ${current + 1}/${total}`;
  }

  async save(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;
    const nextSettings = this.normalizeSettings(this.formState);
    const previousSystemLanguage = this.copilotSettingsService.getSettings().systemLanguage;

    try {
      this.copilotSettingsService.saveSettings(nextSettings);
      this.formState = nextSettings;
      this.toastr.success(this.translationService.translate('Settings saved successfully'));

      if (nextSettings.systemLanguage !== previousSystemLanguage) {
        await this.appLanguageService.changeLanguage(nextSettings.systemLanguage);
      }
    } catch {
      this.toastr.error(this.translationService.translate('Failed to save settings'));
    } finally {
      this.isSaving = false;
    }
  }

  private rebuildBreadcrumb(): void {
    this.breadcrumbItems = [{ label: this.translationService.translate('Settings') }];
  }

  private normalizeSettings(settings: CopilotSettingsPayload): CopilotSettingsPayload {
    const avatarLanguage = settings.avatarLanguage?.trim() || DEFAULT_COPILOT_AVATAR_LANGUAGE;
    const avatarDialect = this.isArabicLanguageCode(avatarLanguage) && this.isAvatarDialect(settings.avatarDialect)
      ? settings.avatarDialect
      : '';

    return {
      speakerLanguage: settings.speakerLanguage?.trim() || DEFAULT_COPILOT_SPEAKER_LANGUAGE,
      avatarLanguage,
      avatarDialect,
      systemLanguage: this.normalizeSystemLanguage(settings.systemLanguage),
    };
  }

  private normalizeSystemLanguage(language: string | null | undefined): string {
    const normalized = (language ?? '').trim().toLowerCase();
    return this.systemLanguages.some(item => item.code === normalized) ? normalized : 'en';
  }

  private parseLanguageOptions(response: any): LanguageOption[] {
    const raw = response?.data ?? response?.content ?? response ?? [];
    if (!Array.isArray(raw)) {
      return [];
    }

    return raw
      .map((item: any) => ({
        id: Number(item?.id),
        name: String(item?.name ?? item?.code ?? ''),
        code: this.getLanguageOptionValue({
          code: String(item?.code ?? ''),
          name: String(item?.name ?? ''),
        }),
        direction: item?.direction ?? null,
      }))
      .filter((item: LanguageOption) => item.code && item.name);
  }

  private getLanguageOptionValue(language: Pick<LanguageOption, 'code' | 'name'>): string {
    return this.isEnglishUkOption(language) ? 'en-GB' : language.code;
  }

  private isEnglishUkOption(language: Pick<LanguageOption, 'code' | 'name'>): boolean {
    const code = this.normalizeLanguageCode(language.code);
    const name = (language.name || '').toLowerCase();
    return code === 'en-gb'
      || code === 'en-uk'
      || /\b(uk|gb|british|england)\b/.test(name);
  }

  private isArabicLanguageCode(code?: string | null): boolean {
    return this.normalizeLanguageCode(code).split('-')[0] === 'ar';
  }

  private normalizeLanguageCode(code?: string | null): string {
    return (code || '').trim().toLowerCase().replace('_', '-');
  }

  private isAvatarDialect(value: string | null | undefined): value is AvatarDialect {
    return this.avatarDialects.some(dialect => dialect.value === value);
  }
}
